import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import OnboardingModal from '../components/OnboardingModal'
import { Icon, RightSidebar } from '../components/Layout'
import PostCard from '../components/PostCard'
import AdCard from '../components/AdCard'
import PinnedAnnouncements from '../components/PinnedAnnouncements'
import { useAuth } from '../context/AuthContext'
import { usePlatformSettings } from '../context/PlatformSettingsContext'
import { useToast } from '../context/ToastContext'
import { computeTrendingFromPosts, normalizeTag } from '../utils/hashtags'
import {
  getFeedPosts,
  getFeedCategories,
  getTrendingTopics,
  createPost as apiCreatePost,
  updatePost as apiUpdatePost,
  deletePost as apiDeletePost,
  likePost as apiLikePost,
  unlikePost as apiUnlikePost,
  createPoll as apiCreatePoll,
} from '../services/posts'
import {
  getSuggestedUsers,
  followUser as apiFollowUser,
  unfollowUser as apiUnfollowUser,
} from '../services/profiles'
import { getPresignedUrl } from '../services/auth'
import { humanizeApiError } from '../services/client'
import { getApprovedAds, recordAdView, recordAdClick } from '../services/marketplace'

// ── GIF Keyboard ─────────────────────────────────────────────
const GIF_CATEGORIES = ['trending', 'reactions', 'memes', 'crypto', 'hype', 'lol']

function GifKeyboard({ onSelect, onClose }) {
  const [activeTab, setActiveTab] = useState('trending')
  const [search, setSearch] = useState('')
  return (
    <div className="absolute bottom-full mb-2 left-0 w-80 bg-surface-container border-2 border-on-background neo-shadow z-50 p-3">
      <div className="flex justify-between items-center mb-2">
        <span className="font-bold text-xs uppercase tracking-widest text-primary-container">GIFs</span>
        <button onClick={onClose} className="text-on-surface-variant hover:text-on-background">
          <Icon name="close" className="text-[18px]" />
        </button>
      </div>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search GIFs…"
        className="w-full bg-background border border-on-background/20 px-3 py-1.5 text-xs mb-2 focus:outline-none focus:border-primary-container text-on-surface"
      />
      <div className="flex gap-1 overflow-x-auto pb-1 mb-2 no-scrollbar">
        {GIF_CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveTab(cat)}
            className={`shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase border transition-colors ${
              activeTab === cat
                ? 'bg-primary-container text-on-primary-fixed border-on-background'
                : 'border-on-background/20 text-on-surface-variant hover:bg-primary-container/10'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>
      <div className="flex flex-col items-center justify-center h-32 text-on-surface-variant gap-2">
        <Icon name="gif_box" className="text-[32px] opacity-30" />
        <p className="text-[10px] uppercase tracking-widest font-bold opacity-50">GIF search coming soon</p>
      </div>
    </div>
  )
}

// ── Poll Creator ──────────────────────────────────────────────
function PollCreator({ onAdd, onClose }) {
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState(['', ''])
  const [duration, setDuration] = useState('1 day')

  const addOption = () => options.length < 4 && setOptions([...options, ''])
  const removeOption = (i) => options.length > 2 && setOptions(options.filter((_, idx) => idx !== i))
  const updateOption = (i, val) => setOptions(options.map((o, idx) => idx === i ? val : o))

  const handleAdd = () => {
    if (!question.trim() || options.filter(o => o.trim()).length < 2) return
    onAdd({ question, options: options.filter(o => o.trim()), duration })
    onClose()
  }

  return (
    <div className="mt-3 bg-background border-2 border-primary-container p-4 relative">
      <div className="flex justify-between items-center mb-3">
        <span className="font-bold text-sm uppercase tracking-widest text-primary-container flex items-center gap-2">
          <Icon name="poll" className="text-[18px]" /> Create Poll
        </span>
        <button onClick={onClose} className="text-on-surface-variant hover:text-on-background">
          <Icon name="close" className="text-[16px]" />
        </button>
      </div>
      <input
        value={question}
        onChange={e => setQuestion(e.target.value)}
        placeholder="Ask a question…"
        className="w-full bg-surface-container border border-on-background/20 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-primary-container text-on-surface"
      />
      <div className="flex flex-col gap-2 mb-3">
        {options.map((opt, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={opt}
              onChange={e => updateOption(i, e.target.value)}
              placeholder={`Option ${i + 1}`}
              className="flex-1 bg-surface-container border border-on-background/20 px-3 py-1.5 text-sm focus:outline-none focus:border-primary-container text-on-surface"
            />
            {options.length > 2 && (
              <button onClick={() => removeOption(i)} className="text-on-surface-variant hover:text-red-500">
                <Icon name="remove_circle" className="text-[18px]" />
              </button>
            )}
          </div>
        ))}
        {options.length < 4 && (
          <button
            onClick={addOption}
            className="flex items-center gap-1 text-primary-container text-xs font-bold hover:underline self-start"
          >
            <Icon name="add_circle" className="text-[16px]" /> Add option
          </button>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-on-surface-variant font-bold">Duration:</span>
          <select
            value={duration}
            onChange={e => setDuration(e.target.value)}
            className="bg-surface-container border border-on-background/20 text-xs px-2 py-1 text-on-surface focus:outline-none"
          >
            {['1 hour', '6 hours', '12 hours', '1 day', '3 days', '7 days'].map(d => (
              <option key={d}>{d}</option>
            ))}
          </select>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-1.5 bg-primary-container text-on-primary-fixed font-bold text-xs border border-on-background neo-border neo-shadow-sm active:scale-95 transition-all"
        >
          Add Poll
        </button>
      </div>
    </div>
  )
}

// ── Post Skeleton ─────────────────────────────────────────────
function PostSkeleton() {
  return (
    <div className="bg-surface-container border border-on-background/10 lg:neo-border p-4 lg:p-6 animate-pulse">
      <div className="flex gap-3 mb-4">
        <div className="w-10 h-10 bg-on-background/10 rounded-none flex-shrink-0" />
        <div className="flex-1 flex flex-col gap-2 pt-1">
          <div className="h-3 bg-on-background/10 w-32" />
          <div className="h-2.5 bg-on-background/10 w-20" />
        </div>
      </div>
      <div className="flex flex-col gap-2 mb-4">
        <div className="h-3 bg-on-background/10 w-full" />
        <div className="h-3 bg-on-background/10 w-5/6" />
        <div className="h-3 bg-on-background/10 w-4/6" />
      </div>
      {/* Reserved image space — prevents layout shift */}
      <div className="w-full bg-on-background/5 mb-4" style={{ aspectRatio: '16/9' }} />
      <div className="flex gap-6 pt-2 border-t border-on-background/10">
        <div className="h-3 bg-on-background/10 w-12" />
        <div className="h-3 bg-on-background/10 w-12" />
        <div className="h-3 bg-on-background/10 w-12" />
      </div>
    </div>
  )
}

// Default categories fallback
const DEFAULT_CATEGORIES = [
  { id: '019e7fba-f9b0-7594-a252-66786bd05a7b', name: 'Technology' },
  { id: '019e7fbb-373a-75ba-ad9c-66ce73900151', name: 'Sports' },
  { id: '019e7fc1-cdc7-731c-bfbe-132a3d2d962d', name: 'meme' },
  { id: '019e7fc3-f5d6-767b-a74b-04617ce24bc1', name: 'crypto insight' },
  { id: '019e7fc4-2056-713e-a41f-14f4346ac78a', name: 'Humor' },
  { id: '019e7fc4-4a4e-7029-b5f9-7d97ad51ccaa', name: 'devlog' },
]

// Local UI key for the "General" chip. If the backend has a real category
// named "General" (case-insensitive), it's resolved to that category's real
// id via generalCategoryId (see resolveFilterCategoryId/resolvePostCategoryId
// below) and behaves exactly like any other category — its own isolated
// feed, reposts included. Only if no such category exists yet does this
// fall back to the legacy "no categoryId = show everything" behaviour.
const GENERAL_FILTER_ID = 'general'

// ── Infinite Scroll Sentinel ──────────────────────────────────
function InfiniteScrollSentinel({ onVisible, enabled }) {
  const ref = useRef(null)
  useEffect(() => {
    if (!enabled) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onVisible() },
      { rootMargin: '400px' } // trigger 400px before sentinel is visible
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [enabled, onVisible])
  return <div ref={ref} className="h-4" />
}

function CategorySelector({ categories, value, onChange, disabled }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const selected = categories.find(cat => cat.id === value) ?? categories[0] ?? null

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        type="button"
        onClick={() => !disabled && setOpen(v => !v)}
        disabled={disabled}
        className="flex min-w-[120px] max-w-[150px] items-center justify-between gap-2 border border-on-background/20 bg-surface-container px-2.5 py-1.5 text-left text-xs font-bold text-on-background focus:outline-none focus:border-primary-container disabled:opacity-50"
      >
        <span className="truncate">{selected?.name ?? 'Select topic'}</span>
        <Icon name={open ? 'expand_less' : 'expand_more'} className="text-[16px] text-on-surface-variant shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/35 md:hidden" onClick={() => setOpen(false)} />
          <div className="fixed inset-x-0 bottom-0 z-50 border-t border-on-background/20 bg-surface-container p-3 shadow-2xl md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-52 md:max-h-72 md:overflow-y-auto md:rounded-none md:border md:p-0 md:shadow-xl">
            <div className="mb-2 flex items-center justify-between md:hidden">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-on-surface-variant">Choose category</span>
              <button type="button" onClick={() => setOpen(false)} className="text-on-surface-variant">
                <Icon name="close" className="text-[18px]" />
              </button>
            </div>
            <div className="max-h-[62vh] overflow-y-auto md:max-h-72">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    onChange(cat.id)
                    setOpen(false)
                  }}
                  className={`flex w-full items-center justify-between gap-2 border-b border-on-background/10 px-3 py-3 text-left text-sm transition-colors last:border-b-0 md:py-2 ${
                    value === cat.id ? 'bg-primary-container/10 text-primary-container font-bold' : 'text-on-background hover:bg-primary-container/10'
                  }`}
                >
                  <span className="truncate">{cat.name ?? cat.label}</span>
                  {value === cat.id && <Icon name="check" className="text-[16px]" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default function Feed() {
  const { user } = useAuth()
  const { isFeatureEnabled } = usePlatformSettings()
  const adsEnabled = isFeatureEnabled('ads')
  const toast = useToast()
  const navigate = useNavigate()

  const needsOnboarding = user && !user.name && !user.displayName && !user.username && !user.fullName
  const [onboardingDone, setOnboardingDone] = useState(false)
  const showOnboarding = needsOnboarding && !onboardingDone

  // ── Feed state ────────────────────────────────────────────
  const [allPosts, setAllPosts] = useState([])
  const [renderedCount, setRenderedCount] = useState(10) // batch render
  const [categories, setCategories] = useState([{ id: GENERAL_FILTER_ID, name: 'General' }, ...DEFAULT_CATEGORIES])
  const [activeFilter, setActiveFilter] = useState(GENERAL_FILTER_ID)
  const [postCategory, setPostCategory] = useState(GENERAL_FILTER_ID)
  const [suggested, setSuggested] = useState([])
  const [trendingTopics, setTrendingTopics] = useState([])

  // firstLoad = true only while the very first 10 posts haven't arrived
  const [firstLoad, setFirstLoad] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [nextCursor, setNextCursor] = useState(null)
  const [error, setError] = useState(null)

  const isFilterUuid = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
  // Filtering now happens server-side (categoryId is passed to getFeedPosts),
  // so allPosts already only contains the active category's posts.
  const posts = allPosts

  // If the backend has a real category literally named "General", use its
  // id everywhere GENERAL_FILTER_ID would otherwise be used. Until then we
  // fall back to the old "no categoryId = show everything" behaviour, which
  // is what caused posts/reposts from every other category to leak into
  // General instead of General being its own real, isolated category.
  const [generalCategoryId, setGeneralCategoryId] = useState(null)

  // As a *filter*: real General id if we have one, else null (whole feed —
  // legacy fallback). As a *post* category: real General id if we have one,
  // else the first real category, so posting never sends a fake id.
  const resolveFilterCategoryId = (catId) =>
    isFilterUuid(catId) ? catId : (catId === GENERAL_FILTER_ID ? generalCategoryId : null)
  const resolvePostCategoryId = (catId) =>
    isFilterUuid(catId)
      ? catId
      : (generalCategoryId ?? categories.find(c => isFilterUuid(c.id))?.id ?? catId)

  // The backend doesn't currently return any trending hashtags, so fall
  // back to deriving them from whatever posts are already loaded.
  const computedTrendingTopics = useMemo(() => computeTrendingFromPosts(allPosts, 8), [allPosts])
  const displayTrendingTopics = trendingTopics.length > 0 ? trendingTopics : computedTrendingTopics

  // ── Composer state ────────────────────────────────────────
  const [postContent, setPostContent] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [selectedGifs, setSelectedGifs] = useState([])
  const [poll, setPoll] = useState(null)
  const [showGifKeyboard, setShowGifKeyboard] = useState(false)
  const [showPollCreator, setShowPollCreator] = useState(false)
  const [posting, setPosting] = useState(false)
  const fileInputRef = useRef(null)
  const feedTopRef = useRef(null)
  const composerRef = useRef(null)
  const composerInputRef = useRef(null)

  useEffect(() => {
    const focusComposer = () => {
      composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setTimeout(() => composerInputRef.current?.focus(), 250)
    }
    window.addEventListener('chicago:focus-composer', focusComposer)
    return () => window.removeEventListener('chicago:focus-composer', focusComposer)
  }, [])

  // ── Initial data fetch ────────────────────────────────────
  useEffect(() => {
    // Sidebar data — non-blocking, runs in background
    Promise.all([getFeedCategories(), getSuggestedUsers(), getTrendingTopics()])
      .then(([catsData, suggestData, trendData]) => {
        if (catsData.length > 0) {
          setCategories([{ id: GENERAL_FILTER_ID, name: 'General' }, ...catsData])
          // Leave activeFilter/postCategory on General — it's the default,
          // not something to override once real categories arrive.
          const realGeneral = catsData.find(
            (c) => (c.name ?? '').trim().toLowerCase() === 'general',
          )
          if (realGeneral) setGeneralCategoryId(realGeneral.id)
        }
        setSuggested(suggestData)
        setTrendingTopics(trendData)
      })
      .catch(() => {})

    // First page only — further pages are fetched lazily as the user
    // scrolls (see InfiniteScrollSentinel / handleLoadMore below), instead
    // of eagerly pulling every post ever created right after first paint.
    setError(null)
    getFeedPosts(null, 20)
      .then((data) => {
        setAllPosts(data.posts ?? [])
        setNextCursor(data.nextCursor ?? null)
        setHasMore(data.hasMore ?? (data.posts ?? []).length === 20)
        setFirstLoad(false)
      })
      .catch(err => {
        setError(err.message || 'Failed to load posts')
        setFirstLoad(false)
      })
  }, [])

  // Refetch from scratch whenever the active category filter changes, using
  // the server-side categoryId filter instead of filtering an entire
  // client-side post history. General (no real id) refetches with no
  // categoryId param, same as the very first load.
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return }
    setFirstLoad(true)
    setError(null)
    getFeedPosts(null, 20, resolveFilterCategoryId(activeFilter))
      .then((data) => {
        setAllPosts(data.posts ?? [])
        setRenderedCount(10)
        setNextCursor(data.nextCursor ?? null)
        setHasMore(data.hasMore ?? (data.posts ?? []).length === 20)
        setFirstLoad(false)
      })
      .catch(err => {
        setError(err.message || 'Failed to load posts')
        setFirstLoad(false)
      })
  }, [activeFilter])

  // The very first load (above) fires before getFeedCategories() resolves,
  // so it can't know the real General id yet and falls back to "no filter"
  // for that one paint. Once categories come back and reveal a real General
  // category, refetch if the user is still sitting on the General tab —
  // otherwise they'd keep seeing every category's posts until they manually
  // switched filters away and back.
  useEffect(() => {
    if (!generalCategoryId || activeFilter !== GENERAL_FILTER_ID) return
    getFeedPosts(null, 20, generalCategoryId)
      .then((data) => {
        setAllPosts(data.posts ?? [])
        setRenderedCount(10)
        setNextCursor(data.nextCursor ?? null)
        setHasMore(data.hasMore ?? (data.posts ?? []).length === 20)
      })
      .catch(() => {})
  }, [generalCategoryId])

  // ── Infinite scroll ───────────────────────────────────────
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !nextCursor) return
    setLoadingMore(true)
    try {
      const data = await getFeedPosts(nextCursor, 20, resolveFilterCategoryId(activeFilter))
      const incoming = data.posts ?? []
      setAllPosts(prev => {
        const existingIds = new Set(prev.map(p => p.id))
        return [...prev, ...incoming.filter(p => !existingIds.has(p.id))]
      })
      setRenderedCount(c => c + incoming.length)
      setNextCursor(data.nextCursor ?? null)
      if (!data.hasMore || incoming.length === 0) setHasMore(false)
    } catch {
      // silent
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, hasMore, nextCursor, activeFilter])

  // Reveal posts as they come in (first page renders progressively too)
  useEffect(() => {
    if (allPosts.length > renderedCount) {
      setRenderedCount(c => Math.min(c + 10, allPosts.length))
    }
  }, [allPosts.length])

  const visiblePosts = posts.slice(0, renderedCount)

  // ── Sponsored ad injection ────────────────────────────────
  // Approved ads come from the backend's mirror of the marketplace
  // contract (see services/marketplace.js getApprovedAds) — the feed
  // never scans the chain directly.
  //
  // An ad is inserted every AD_INJECT_INTERVAL posts (never as the very
  // first card — the feed should always open on a real post), repeating
  // for as long as the person keeps scrolling, the way most feed products
  // space sponsored content.
  //
  // Rotation is strict round-robin, not random: slot 1 gets feedAds[0],
  // slot 2 gets feedAds[1], etc., wrapping back to feedAds[0] once it runs
  // out. With real money behind each ad, round-robin guarantees every
  // advertiser gets mathematically equal exposure — a weighted-random pick
  // can leave one advertiser unlucky for an entire session purely by
  // chance, which isn't a fair outcome for something people paid for.
  const AD_INJECT_INTERVAL = 8
  const [feedAds, setFeedAds] = useState([])

  // When the admin sets Ads to "Coming Soon", don't inject sponsored cards.
  useEffect(() => {
    if (!adsEnabled) { setFeedAds([]); return }
    getApprovedAds('feed').then(setFeedAds).catch(() => setFeedAds([]))
  }, [adsEnabled])

  const handleAdView = useCallback((adId) => {
    recordAdView(adId)
  }, [])

  const handleAdClick = useCallback((adId) => {
    recordAdClick(adId)
  }, [])

  // Posts with a sponsored card spliced in every AD_INJECT_INTERVAL posts,
  // cycling through feedAds round-robin. Recomputes whenever the visible
  // post list or ad list changes — splice indices are stable because each
  // ad slot's position only depends on how many *posts* precede it, not on
  // anything random, so this never reshuffles existing cards as more posts
  // load in via infinite scroll.
  const feedItems = useMemo(() => {
    if (feedAds.length === 0) {
      return visiblePosts.map(post => ({ type: 'post', post }))
    }
    const items = []
    let adCursor = 0
    visiblePosts.forEach((post, i) => {
      // i > 0 ensures the very first card is always a real post.
      if (i > 0 && i % AD_INJECT_INTERVAL === 0) {
        items.push({ type: 'ad', ad: feedAds[adCursor % feedAds.length] })
        adCursor++
      }
      items.push({ type: 'post', post })
    })
    return items
  }, [visiblePosts, feedAds])

  // ── Like handler ──────────────────────────────────────────
  const handleLike = async (postId) => {
    const post = allPosts.find(p => p.id === postId)
    if (!post) return
    setAllPosts(prev => prev.map(p =>
      p.id === postId
        ? { ...p, liked: !p.liked, likes: Math.max(0, p.liked ? (Number(p.likes) || 0) - 1 : (Number(p.likes) || 0) + 1) }
        : p
    ))
    try {
      if (post.liked) await apiUnlikePost(postId)
      else await apiLikePost(postId)
    } catch {
      setAllPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, liked: post.liked, likes: post.likes } : p
      ))
    }
  }

  const handleEditPost = async (post) => {
    const next = window.prompt('Edit post:', post.content ?? '')
    if (next === null || next.trim() === (post.content ?? '').trim()) return
    const prev = allPosts
    setAllPosts(p => p.map(x => x.id === post.id ? { ...x, content: next.trim() } : x))
    try {
      await apiUpdatePost(post.id, { content: next.trim(), categoryId: post.categoryId, isPublished: true })
    } catch (err) {
      setAllPosts(prev)
      toast.error(humanizeApiError(err))
    }
  }

  const handleDeletePost = async (post) => {
    const prev = allPosts
    setAllPosts(p => p.filter(x => x.id !== post.id))
    try {
      await apiDeletePost(post.id)
    } catch {
      setAllPosts(prev)
      toast.error('Could not delete post.')
    }
  }

  const handleFollow = async (userId) => {
    const su = suggested.find(u => u.id === userId)
    if (!su) return
    setSuggested(prev => prev.map(u => u.id === userId ? { ...u, following: !u.following } : u))
    try {
      if (su.following) await apiUnfollowUser(userId)
      else await apiFollowUser(userId)
    } catch {
      setSuggested(prev => prev.map(u => u.id === userId ? { ...u, following: su.following } : u))
      toast.error('Could not update follow status.')
    }
  }

  const handleHashtagClick = (hashtag) => {
    navigate(`/hashtag/${encodeURIComponent(normalizeTag(hashtag))}`)
  }

  // ── File handlers ─────────────────────────────────────────
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    const previews = files.map(f => ({
      file: f,
      name: f.name,
      url: URL.createObjectURL(f),
      contentType: f.type || 'application/octet-stream',
    }))
    setSelectedFiles(prev => [...prev, ...previews])
    e.target.value = ''
  }

  const removeFile = (i) => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))
  const removeGif = (i) => setSelectedGifs(prev => prev.filter((_, idx) => idx !== i))

  // ── Post handler ──────────────────────────────────────────
  const handlePost = async () => {
    setPosting(true)
    try {
      if (poll) {
        if (!postCategory) { toast.error('Categories loading, please wait.'); setPosting(false); return }
        await apiCreatePoll({
          question: poll.question, options: poll.options,
          duration: poll.duration, categoryId: resolvePostCategoryId(postCategory),
          content: postContent.trim() || poll.question,
        })
        // NOTE: previously this built a fake "optimistic" post with made-up
        // ids like `optimistic-poll-...` / `optimistic-opt-0`. Those aren't
        // real UUIDs, so when the user tapped an option, votePoll() called
        // POST /poll/optimistic-poll-.../vote — the backend's ParseUUIDPipe
        // rejects that immediately and the vote always reverted to 0.
        // Refetching the feed (same as the normal-post path below) gives us
        // the real, backend-assigned poll id and option ids so voting works.
        const data = await getFeedPosts(null, 20, resolveFilterCategoryId(activeFilter))
        setAllPosts(data.posts ?? [])
        setNextCursor(data.nextCursor ?? null)
        setHasMore(data.hasMore ?? true)
        setRenderedCount(10)
        setPostContent(''); setSelectedFiles(prev => { prev.forEach(f => f.url && URL.revokeObjectURL(f.url)); return [] })
        setSelectedGifs([]); setPoll(null); setShowGifKeyboard(false); setShowPollCreator(false)
        setPosting(false)
        return
      }

      if (!postContent.trim() && selectedFiles.length === 0 && selectedGifs.length === 0) { setPosting(false); return }
      if (!postCategory) { toast.error('Categories loading, please wait.'); setPosting(false); return }

      const mediaUrls = []
      for (let i = 0; i < selectedFiles.length; i++) {
        const { file, name, contentType } = selectedFiles[i]
        let presigned
        try { presigned = await getPresignedUrl({ fileName: name, contentType, type: 'post' }) }
        catch (err) { toast.error(`Image upload failed: ${err.message}`); setPosting(false); return }
        const { uploadUrl, url: publicUrl, key: publicId } = presigned?.data ?? presigned
        const s3Res = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file })
        if (!s3Res.ok) { toast.error(`Failed to upload ${name}`); setPosting(false); return }
        mediaUrls.push({ url: publicUrl, publicId: publicId ?? name, position: i + 1, type: contentType.startsWith('video') ? 'video' : 'image' })
      }
      for (let i = 0; i < selectedGifs.length; i++) {
        mediaUrls.push({ url: selectedGifs[i].url, publicId: selectedGifs[i].id ?? `gif-${i}`, position: selectedFiles.length + i + 1, type: 'image' })
      }

      await apiCreatePost({ content: postContent, categoryId: resolvePostCategoryId(postCategory), isPublished: true, ...(mediaUrls.length > 0 && { mediaUrls }) })

      const data = await getFeedPosts(null, 20, resolveFilterCategoryId(activeFilter))
      setAllPosts(data.posts ?? [])
      setNextCursor(data.nextCursor ?? null)
      setHasMore(data.hasMore ?? true)
      setRenderedCount(10)
      setPostContent('')
      setSelectedFiles(prev => { prev.forEach(f => f.url && URL.revokeObjectURL(f.url)); return [] })
      setSelectedGifs([]); setPoll(null); setShowGifKeyboard(false); setShowPollCreator(false)
    } catch (err) {
      toast.error(humanizeApiError(err))
    } finally {
      setPosting(false)
    }
  }

  return (
    <>
      {showOnboarding && <OnboardingModal onComplete={() => setOnboardingDone(true)} />}
      <div ref={feedTopRef} className="flex-1 lg:ml-[236px] lg:mr-[276px] flex flex-col gap-4 lg:gap-6 min-w-0 overflow-x-hidden lg:px-4">
        <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFileChange} />

        {/* ── Compose Box ── */}
        <section ref={composerRef} className="bg-surface-container border border-on-background/10 lg:neo-border lg:neo-shadow p-3 lg:p-6">
          <div className="flex gap-3">
            <img src={user?.avatar || '/favicon.jpg'} alt="Me" className="w-9 h-9 lg:w-12 lg:h-12 border border-on-background/20 lg:neo-border object-cover" />
            <div className="flex-1">
              <textarea
                ref={composerInputRef}
                value={postContent}
                onChange={e => setPostContent(e.target.value)}
                onPaste={(e) => {
                  const items = Array.from(e.clipboardData?.items ?? [])
                  const imageItems = items.filter(it => it.kind === 'file' && it.type.startsWith('image/'))
                  if (imageItems.length === 0) return
                  e.preventDefault()
                  const files = imageItems.map(it => it.getAsFile()).filter(Boolean)
                  setSelectedFiles(prev => [...prev, ...files.map(f => ({
                    file: f, name: f.name || `paste-${Date.now()}.png`,
                    url: URL.createObjectURL(f), contentType: f.type || 'image/png',
                  }))])
                }}
                className="w-full bg-background border border-on-background/15 lg:neo-border p-2 lg:p-4 text-sm lg:text-body-md focus:outline-none min-h-[70px] lg:min-h-[100px] resize-none placeholder:text-on-surface-variant text-on-surface"
                placeholder="What's on your mind?"
              />
              {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedFiles.map((f, i) => (
                    <div key={i} className="relative group">
                      {f.url.match(/\.(mp4|webm)$/i)
                        ? <video src={f.url} className="w-20 h-20 object-cover border border-on-background/20" />
                        : <img src={f.url} alt={f.name} className="w-20 h-20 object-cover border border-on-background/20" loading="lazy" />}
                      <button onClick={() => removeFile(i)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">✕</button>
                    </div>
                  ))}
                </div>
              )}
              {selectedGifs.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedGifs.map((g, i) => (
                    <div key={i} className="relative group">
                      <img src={g} alt="gif" className="w-20 h-20 object-cover border border-primary-container/40" loading="lazy" />
                      <span className="absolute bottom-0 left-0 bg-primary-container text-on-primary-fixed text-[8px] font-bold px-1">GIF</span>
                      <button onClick={() => removeGif(i)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">✕</button>
                    </div>
                  ))}
                </div>
              )}
              {poll && (
                <div className="mt-3 bg-background border border-primary-container/40 p-3">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-bold text-sm text-on-background">{poll.question}</p>
                    <button onClick={() => setPoll(null)} className="text-on-surface-variant hover:text-red-500 ml-2"><Icon name="close" className="text-[16px]" /></button>
                  </div>
                  {poll.options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 border-2 border-on-background/40 rounded-full" />
                      <span className="text-xs text-on-surface-variant">{opt}</span>
                    </div>
                  ))}
                  <p className="text-[10px] text-primary-container mt-1 font-mono">⏱ {poll.duration}</p>
                </div>
              )}
              <div className="flex justify-between items-center mt-2 lg:mt-4 relative">
                <div className="flex gap-2 lg:gap-4 text-on-surface-variant relative">
                  <button onClick={() => fileInputRef.current?.click()} title="Attach image" className="hover:text-primary-container transition-colors">
                    <Icon name="image" className="cursor-pointer text-[18px] lg:text-[24px]" />
                  </button>
                  <div className="relative">
                    <button onClick={() => { setShowGifKeyboard(v => !v); setShowPollCreator(false) }} title="Add GIF" className={`hover:text-primary-container transition-colors ${showGifKeyboard ? 'text-primary-container' : ''}`}>
                      <Icon name="gif_box" className="cursor-pointer text-[18px] lg:text-[24px]" />
                    </button>
                    {showGifKeyboard && <GifKeyboard onSelect={(gif) => setSelectedGifs(prev => [...prev, gif])} onClose={() => setShowGifKeyboard(false)} />}
                  </div>
                  <button onClick={() => { setShowPollCreator(v => !v); setShowGifKeyboard(false) }} title="Create poll" className={`hover:text-primary-container transition-colors ${showPollCreator ? 'text-primary-container' : ''}`}>
                    <Icon name="poll" className="cursor-pointer text-[18px] lg:text-[24px]" />
                  </button>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <CategorySelector
                    categories={categories}
                    value={postCategory ?? GENERAL_FILTER_ID}
                    onChange={setPostCategory}
                    disabled={!postCategory}
                  />
                  <button onClick={handlePost} disabled={posting} className="shrink-0 px-4 lg:px-8 py-1.5 lg:py-2 bg-primary-container text-on-primary-fixed font-bold text-sm lg:text-base border border-on-background/20 lg:neo-border lg:neo-shadow-sm active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                    {posting ? <><div className="w-4 h-4 border-2 border-current border-t-transparent animate-spin" />{selectedFiles.length > 0 ? 'Uploading…' : 'Posting…'}</> : 'Post'}
                  </button>
                </div>
              </div>
              {showPollCreator && !poll && (
                <PollCreator onAdd={(p) => { setPoll(p); setShowPollCreator(false) }} onClose={() => setShowPollCreator(false)} />
              )}
            </div>
          </div>
        </section>

        {/* ── Pinned announcements (above category tabs so they show on every category) ── */}
        <PinnedAnnouncements />

        {/* ── Category Filters ── */}
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveFilter(cat.id)}
              className={`flex-none whitespace-nowrap px-4 py-1.5 text-sm font-bold border border-on-background/20 lg:neo-border transition-colors ${
                activeFilter === cat.id ? 'bg-primary-container text-on-primary-fixed lg:neo-shadow-sm' : 'bg-surface-container hover:bg-primary-container/20 text-on-background'
              }`}
            >
              {cat.name ?? cat.label}
            </button>
          ))}
        </div>

        {/* ── Posts ── */}
        {error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-on-surface-variant">
            <span className="material-symbols-outlined text-[40px] opacity-30">wifi_off</span>
            <p className="font-bold text-sm uppercase tracking-widest opacity-50">Unable to load posts</p>
            <p className="text-xs opacity-40">{error}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {/* First load — show 10 skeletons immediately, replace when data arrives */}
            {firstLoad ? (
              Array.from({ length: 10 }).map((_, i) => <PostSkeleton key={i} />)
            ) : (
              <>
                {feedItems.map((item, i) => (
                  item.type === 'ad'
                    ? <AdCard key={`ad-slot-${i}-${item.ad.id}`} ad={item.ad} onView={handleAdView} onClick={handleAdClick} />
                    : <PostCard key={item.post.id} post={item.post} onLike={handleLike} onEdit={handleEditPost} onDelete={handleDeletePost} onHashtagClick={handleHashtagClick} />
                ))}
                {visiblePosts.length === 0 && (
                  <p className="text-center text-on-surface-variant py-12">No posts in this category yet.</p>
                )}
                {/* Inline skeletons for loading-more state */}
                {loadingMore && (
                  <>
                    <PostSkeleton />
                    <PostSkeleton />
                  </>
                )}
                {/* Invisible sentinel triggers infinite scroll */}
                <InfiniteScrollSentinel
                  onVisible={handleLoadMore}
                  enabled={hasMore && !loadingMore && !firstLoad}
                />
                {!hasMore && visiblePosts.length > 0 && (
                  <p className="text-center text-[11px] text-on-surface-variant/40 font-bold uppercase tracking-widest py-4">
                    You're all caught up
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div className="h-20 md:h-0" />
        <RightSidebar
          suggestedUsers={suggested}
          trendingTopics={displayTrendingTopics}
          onFollow={handleFollow}
          onHashtagClick={handleHashtagClick}
        />
      </div>
    </>
  )
}
